import React, { useState, useEffect } from "react";
import { Spinner, Text, Box } from "@chakra-ui/react";
import MetricCard from "./MetricCard";
import { metricsService, SopDeviationSummary } from "../../services/metricsService";

interface SOPDeviationCardProps {
  onMetricSelect?: (metricName: string) => void;
  businessIdentifier?: string;
  timeRange?: string;
  value?: number;
  trendValue?: number;
}

const SOPDeviationCard: React.FC<SOPDeviationCardProps> = ({ 
  onMetricSelect, 
  businessIdentifier,
  timeRange = '7d',
  value: propValue,
  trendValue: propTrendValue 
}) => {
  const [sopData, setSopData] = useState<SopDeviationSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSopData = async () => {
      // If prop values are provided, don't fetch from API (for backward compatibility)
      if (propValue !== undefined && propTrendValue !== undefined) {
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const data = await metricsService.getSopDeviationSummary(businessIdentifier, timeRange);
        setSopData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load SOP deviation data.');
        console.error('Error fetching SOP data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSopData();
  }, [businessIdentifier, timeRange, propValue, propTrendValue]);

  const handleCompare = () => {
    if (onMetricSelect) {
      onMetricSelect("SOP Deviation");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Text color="red.500" textAlign="center">{error}</Text>
      </Box>
    );
  }

  // Use prop values if provided, otherwise use API data, otherwise use fallback
  const displayValue = propValue !== undefined ? propValue : 
    (sopData ? (100 - sopData.compliance_percentage) : 62.4);
  const trendDisplayValue = propTrendValue !== undefined ? propTrendValue :
    (sopData?.trend !== undefined ? sopData.trend : 3);

  return (
    <MetricCard
      title="SOP Deviation"
      value={displayValue}
      trendValue={trendDisplayValue}
      onCompare={handleCompare}
    />
  );
};

export default SOPDeviationCard; 